<template>
    <div class="page-container checkout-stripe">
        <transition name="fade">
            <div v-show="loading" class="loading-overlay">
                <loader></loader>
            </div>
        </transition>
        <div class="checkout-header">
            <ul class="steps">
                <li v-bind:class="{active: active_step == 1}" @click="moveTo(1)">1</li>
                <li v-bind:class="{active: active_step == 2}" @click="moveTo(2)">2</li>
                <li v-bind:class="{active: active_step == 3}">3</li>
            </ul>
            <span class="title">Checkout</span>
            <div class="actions">
                <a class="close" @click="goBack"></a>
            </div>
        </div>
        <div v-show="!loading">
            <div id="payment-request">
                <div id="payment-request-button"></div>
            </div>
            <form id="payment-form" class="stripe-payment-form" @submit="pay">
                <!-- Shipping information step -->
                <section class="checkout-step" v-bind:class="{active: active_step == 1}">
                    <h2 class="stripe-fieldset-header">Shipping Details</h2>
                    <fieldset class="stripe-fieldset" v-for="i in 1" :key="i">
                        <checkout-field
                            label="First Name"
                            v-model="shippingAddress.first_name"
                            ref="shippingAddressField">
                        </checkout-field>
                        <checkout-field
                            label="Last Name"
                            v-model="shippingAddress.last_name"
                            ref="shippingAddressField">
                        </checkout-field>
                        <checkout-field
                            label="Email"
                            type="email"
                            v-model="shippingAddress.email"
                            ref="shippingAddressField">
                        </checkout-field>
                        <checkout-country-select
                            v-model="shippingAddress.country_id"
                            v-bind:countries="availableCountries"
                            ref="shippingAddressField">
                        </checkout-country-select>
                        <checkout-field
                            label="Street address"
                            v-model="shippingAddress.address_street_1"
                            ref="shippingAddressField">
                        </checkout-field>
                        <checkout-field
                            label="City"
                            v-model="shippingAddress.address_city"
                            ref="shippingAddressField">
                        </checkout-field>
                        <checkout-field
                            label="Postal code"
                            v-model="shippingAddress.address_zip"
                            @input="v => { shippingAddress.address_zip = v.toUpperCase()}" 
                            :validator="(value) => validateZipCode(value, getSelectedShippingCountry)"
                            ref="shippingAddressField">
                        </checkout-field>
                        <checkout-state-select
                            v-if="availableStatesForShipping && availableStatesForShipping.length > 0"
                            v-model="shippingAddress.state_id" 
                            v-bind:states="availableStatesForShipping" 
                            v-bind:countryid="shippingAddress.country_id"
                            ref="shippingAddressField">
                        </checkout-state-select>
                        <checkout-field
                            label="Phone"
                            v-model="shippingAddress.phone"
                            ref="shippingAddressField">
                        </checkout-field>
                        <div class="button-wrapper">
                            <button class="button pink full-width" type="button" @click="saveShippingStep">Continue</button>
                        </div>
                    </fieldset>
                </section>

                <!-- Billing information step -->
                <section class="checkout-step" v-bind:class="{active: active_step == 2}">
                    <h2 class="stripe-fieldset-header">Discount</h2>
                    <div class="payment-info visible">
                        <div class="stripe-fieldset discount-code-field">
                            <checkout-field
                                label="Discount code"
                                v-model="discount_code"
                                :required="false"
                                ref="discountCodeField">
                            </checkout-field>
                            <button class="button pink" type="button"
                                @click="applyDiscountCode"
                                :disabled="!discount_code && !cartTotals.discount_code"
                                v-if="!cartTotals.discount_code">Apply</button>
                            <button class="button pink" type="button"
                                @click="removeDiscountCode"
                                v-if="cartTotals.discount_code">Remove</button>
                        </div>
                        <div id="discount-code-message" class="success" v-if="discount_code_message">{{ discount_code_message }}</div>
                        <div id="discount-code-error" role="alert" class="error" v-if="discount_code_error">{{ discount_code_error }}</div>
                    </div>
                    <h2 class="stripe-fieldset-header">Payment Information</h2>
                    <div class="payment-info card visible">
                        <fieldset class="stripe-fieldset">
                            <div class="stripe-field-container">
                                <div id="card-element" class="stripe-field" ref="card"></div>
                            </div>
                            <div id="card-errors" role="alert" class="error">{{ card_error }}</div>
                        </fieldset>
                    </div>
                    <h2 class="stripe-fieldset-header">Billing Details</h2>
                    <div class="stripe-field-container address-selector" v-bind:class="{selected: same_as_shipping}" @click="changeSameAsShipping(true)">
                        <div class="radio"></div>
                        <div class="details">
                            <span class="label">Use shipping address</span>
                            <span class="content">{{ short_shipping_address }}</span>
                        </div>
                    </div>
                    <div class="stripe-field-container address-selector" v-bind:class="{selected: !same_as_shipping}" @click="changeSameAsShipping(false)">
                        <div class="radio"></div>
                        <div class="details">
                            <span class="content">New address</span>
                        </div>
                    </div>
                    <fieldset class="stripe-fieldset" v-if="same_as_shipping == false" v-for="i in 1" :key="i">
                        <checkout-field
                            label="First Name"
                            v-model="billingAddress.first_name"
                            ref="billingAddressField">
                        </checkout-field>
                        <checkout-field
                            label="Last Name"
                            v-model="billingAddress.last_name"
                            ref="billingAddressField">
                        </checkout-field>
                        <checkout-field
                            label="Email"
                            type="email"
                            v-model="billingAddress.email"
                            ref="billingAddressField">
                        </checkout-field>
                        <checkout-country-select
                            v-model="billingAddress.country_id"
                            v-bind:countries="availableCountries"
                            ref="billingAddressField">
                        </checkout-country-select>
                        <checkout-field
                            label="Street address"
                            v-model="billingAddress.address_street_1"
                            ref="billingAddressField">
                        </checkout-field>
                        <checkout-field
                            label="City"
                            v-model="billingAddress.address_city"
                            ref="billingAddressField">
                        </checkout-field>
                        <checkout-field
                            label="Postal code"
                            v-model="billingAddress.address_zip"
                            @input="v => { billingAddress.address_zip = v.toUpperCase()}" 
                            :validator="(value) => validateZipCode(value, getSelectedBillingCountry)"
                            ref="billingAddressField">
                        </checkout-field>
                        <checkout-state-select
                            v-if="availableStatesForBilling && availableStatesForBilling.length > 0"
                            v-model="billingAddress.state_id"
                            v-bind:states="availableStatesForBilling"
                            v-bind:countryid="billingAddress.country_id"
                            ref="billingAddressField">
                        </checkout-state-select>
                        <checkout-field
                            label="Phone"
                            v-model="billingAddress.phone"
                            ref="billingAddressField">
                        </checkout-field>
                    </fieldset>
                    <div class="button-wrapper">
                        <button class="button back-button full-width" type="button" @click="moveTo(1)">Back</button>
                        <button class="button pink full-width" type="button" @click="saveBillingStep">Continue</button>
                    </div>
                </section>

                <!-- Review step -->
                <section class="checkout-step" v-bind:class="{active: active_step == 3}">
                    <h2 class="stripe-fieldset-header">Shipping</h2>
                    <button class="stripe-step-review" type="button" @click="goToStep(1)">
                        <span>{{ short_shipping_address }}</span>
                    </button>
                    <h2 class="stripe-fieldset-header">Payment</h2>
                    <button class="stripe-step-review" type="button" @click="goToStep(2)">
                        <span>{{ card_type }} card</span>
                    </button>
                    <h2 class="stripe-fieldset-header">Order</h2>
                    <button class="stripe-step-review" type="button" @click="goToStep(4)">
                        <span>{{ cartProductsCount }} items</span>
                    </button>
                    <table class="checkout-totals">
                        <thead>
                            <tr>
                                <th></th><th width=1></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Subtotal</td>
                                <td>{{ cartTotals.subtotal | currency }}</td>
                            </tr>
                            <tr v-if="cartTotals.discount < 0">
                                <td>Discount</td>
                                <td>{{ cartTotals.discount | currency }}</td>
                            </tr>
                            <tr>
                                <td>Shipping</td>
                                <td>{{ cartTotals.shipping | currency }}</td>
                            </tr>
                            <tr>
                                <td>Taxes and Handling</td>
                                <td>{{ (cartTotals.tax + cartTotals.handling_fee) | currency }}</td>
                            </tr>
                            <tr v-if="cartTotals.giftcard_amount > 0">
                                <td>Giftcard Amount</td>
                                <td>{{ -1 * cartTotals.giftcard_amount | currency }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="button-wrapper">
                        <button class="button pink full-width payment-button" type="submit">Pay {{ cartTotals.grand_total | currency }}</button>
                    </div>
                </section>

                <section class="checkout-step" v-bind:class="{active: active_step == 4}">
                    <a class="back" @click="goToStep(3)">Go back</a>
                    <div class="cart-items-container">
                        <div class="cart-outfit-container"
                            v-for="(outfit, outfitKey, index) in cartOutfits"
                            :key="outfit.outfit_id"
                        >
                            <transition-group name="cart-item">
                                {{ index }}
                                <div class="cart-item"
                                    v-for="product in outfit"
                                    :key="product.cart_product_id"
                                >
                                    <div class="wtw-container small">
                                        <div class="cart-info-container">
                                            <div class="cart-item-image"
                                                :style="{ backgroundImage: 'url(' + product.product_image_url + ')' }">
                                            </div>
                                            <div class="cart-item-info">
                                                <div v-if="product.product_brand_title" class="brand">{{ product.product_brand_title }}</div>
                                                <div class="title"> {{ product.product_title }}</div>
                                                <div class="price"> <span class="inline-title">Price:</span> {{ product.product_price | currency }}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="quantity-row wtw-container small">
                                        <div class="small">
                                            <span>Qty: {{ product.cart_product_quantity }}</span>
                                        </div>
                                        <div class="subtotal"> <span class="inline-title">Subtotal:</span> {{ getRowTotal(product) | currency }}</div>
                                    </div>
                                </div>
                            </transition-group>
                        </div>
                    </div>
                </section>
            </form>
        </div>
    </div>
</template>

<script>
import {elements, stripe} from '../../utilities/stripe';
import {mapGetters} from 'vuex';

import Loader from "../common/loader.vue";
import CheckoutField from './checkout-field.vue';
import CheckoutCountrySelect from './checkout-country-select.vue';
import CheckoutStateSelect from './checkout-state-select.vue';
import { trackEvent, eventNames } from '../../store/utils/tracking';
import Validation from '../../mixins/validation';

export default {
    components: {
        Loader,
        CheckoutField,
        CheckoutCountrySelect,
        CheckoutStateSelect,
    },
    mixins: [Validation],

    data() {
        return {
            card: null,
            card_type: '',
            card_last4: '',
            card_error: '',
            discount_code: '',
            discount_code_message: '',
            discount_code_error: '',
            loading: true,
            same_as_shipping: true,
            active_step: 1,
            previous_step: null,
            short_shipping_address: ''
        }
    },

    computed: {
        availableStatesForShipping() {
            return this.availableStates[this.getSelectedShippingCountry?.country_code];
        },
        availableStatesForBilling() {
            return this.availableStates[this.getSelectedBillingCountry?.country_code];
        },
        billingDetails() {
            return {
                name: this.billingAddress.first_name + " " + this.billingAddress.last_name,
                email: this.billingAddress.email,
                phone: this.billingAddress.phone,
                address: {
                    line1: this.billingAddress.address_street_1,
                    line2: this.billingAddress.address_street_2,
                    city: this.billingAddress.address_city,
                    postal_code: this.billingAddress.address_zip,
                    state: this.getSelectedBillingState?.state_code || this.getSelectedBillingState?.state_name || '',
                    country: this.getSelectedBillingCountry?.country_code
                }
            }
        },
        shippingDetails() {
            return {
                name: this.shippingAddress.first_name + " " + this.shippingAddress.last_name,
                phone: this.shippingAddress.phone,
                address: {
                    line1: this.shippingAddress.address_street_1,
                    line2: this.shippingAddress.address_street_2,
                    city: this.shippingAddress.address_city,
                    postal_code: this.shippingAddress.address_zip,
                    state: this.getSelectedShippingState?.state_code || this.getSelectedShippingState?.state_name || '',
                    country: this.getSelectedShippingCountry?.country_code
                }
            }
        },
        activeStepClass: function(step) {
            return {
                active: (this.active_step == step)
            }
        },
        getSelectedBillingCountry() {
            for(var idx in this.availableCountries) {
                if(this.availableCountries[idx].country_id == this.billingAddress.country_id) {
                    return this.availableCountries[idx];
                }
            }
        },
        getSelectedShippingCountry() {
            for(var idx in this.availableCountries) {
                if(this.availableCountries[idx].country_id == this.shippingAddress.country_id) {
                    return this.availableCountries[idx];
                }
            }
        },

        getSelectedBillingState() {
            for(var idx in this.availableStatesForBilling) {
                if(this.availableStatesForBilling[idx].state_id == this.billingAddress.state_id) {
                    return this.availableStatesForBilling[idx];
                }
            }
        },

        getSelectedShippingState() {
            for(var idx in this.availableStatesForShipping) {
                if(this.availableStatesForShipping[idx].state_id == this.shippingAddress.state_id) {
                    return this.availableStatesForShipping[idx];
                }
            }
        },
        ...mapGetters({
            paymentIntent: 'checkout/getStripePaymentIntent',
            cartTotals: 'checkout/getCartTotals',
            billingAddress: 'checkout/getCartBillingAddress',
            shippingAddress: 'checkout/getCartShippingAddress',
            availableCountries: 'checkout/getAvailableCountries',
            availableStates: 'checkout/getAvailableStates',
            cartOutfits: 'cart/getCartOutfits',
            cartProductsCount: 'cart/cartProductsCount'
        })
    },

    created() {
        this.$store
            .dispatch('checkout/createStripePaymentIntent', { billingAddress: null, shippingAddress: null } )
            .then(() => {
                this.loading = false;
                this.discount_code = this.cartTotals.discount_code;
                
                this.$helpers.trackEvent(eventNames.CHECKOUT_INIT, {
                    num_items: this.cartProductsCount,
                    content_type: 'product',
                    value: this.cartTotals.grand_total,
                    items: Object.values(this.cartOutfits).map(outfit => {
                        return Object.values(outfit).map(product => ({
                            'item_brand': product['product_brand_title'] || '',
							'item_category': product['product_category_title'] || '',
							'item_id': product['product_variant_sku'] || product['product_sku'],
							'item_name': product['product_title'],
							'price': product['product_price'] || 0,
							'quantity': product['cart_product_quantity']
                        }));
                    }).flat(),
                });
            })
            .catch(() => this.$router.push('/cart'));
    },

    mounted() {
        if(!elements) {
            this.$router.back();
            if(typeof window.FirebasePlugin !== 'undefined') window.FirebasePlugin.logEvent("stripe_elements_not_found", {});
            return;
        }
        if(!this.card && this.$refs.card) {
            this.card = elements.create('card');
            this.card.mount(this.$refs.card);

            this.card.addEventListener('change', function(event) {
                if (event.error) {
                    this.card_error = event.error.message;
                } else {
                    this.card_error = '';
                }
                this.card_type = event.brand;
            }.bind(this))
        }
        document.body.classList.add('no-header');
    },

    beforeDestroy() {
        if(this.card) {
            this.card.destroy(this.$refs.card);
        }

        document.body.classList.remove('no-header');
    },

    methods: {
        async pay(event) {
            event.preventDefault();
            if (this.paymentIntent && this.cartTotals.grand_total > 0) {
                if (!this.card_error) {
                    this.loading = true;
                    this.$store
                        .dispatch('checkout/confirmStripePayment', { card: this.card, billingDetails: this.billingDetails, shippingDetails: this.shippingDetails })
                        .then((response) => {
                            if(!response.error) {
                                this.$store
                                    .dispatch('checkout/saveCart', {
                                        billingAddress: this.billingAddress,
                                        shippingAddress: this.shippingAddress
                                    })
                                    .then((response) => {
                                        this.$store.commit('cart/resetState');

                                        this.$store.commit('cart/saveSuccessCart', response);
                                        this.resetDiscountCode();
                                    });

                                this.$router.push('/checkout/thank-you');
                            } else {
                                this.card_error = response.error.message;
                                this.active_step = 2;
                                this.loading = false;
                                this.$nextTick(this.scrollToError());
                            }
                        })
                        .catch((error) => {
                            this.card_error = error.message;
                            this.active_step = 2;
                            this.loading = false;
                            this.$nextTick(this.scrollToError());
                        });
                } else {
                    this.active_step = 2;
                }
            } else if (!this.paymentIntent && this.cartTotals.grand_total == 0) {
                this.$store
                    .dispatch('checkout/saveCart', {
                        billingAddress: this.billingAddress,
                        shippingAddress: this.shippingAddress
                    })
                    .then((response) => {
                        this.$store.commit('cart/resetState');

                        this.$store.commit('cart/saveSuccessCart', response);
                        this.resetDiscountCode();
                    });

                this.$router.push('/checkout/thank-you');
            }
        },

        collectTotals(event) {
            this.loading = true;
            this.$store
                .dispatch('checkout/createStripePaymentIntent', {
                    billingAddress: this.billingAddress,
                    shippingAddress: this.shippingAddress
                })
                .then(() => {
                    this.loading = false;
                });
        },
        resetDiscountCode() {
            this.discount_code = '';
            this.discount_code_message = '';
            this.discount_code_error = '';
        },
        changeSameAsShipping(selected) {
            this.same_as_shipping = selected;
            this.setSameAsShipping(false);  
        },
        setSameAsShipping(reset = false) {
            if(this.same_as_shipping) {
                this.billingAddress.first_name = this.shippingAddress.first_name;
                this.billingAddress.last_name = this.shippingAddress.last_name;
                this.billingAddress.email = this.shippingAddress.email;
                this.billingAddress.phone = this.shippingAddress.phone;
                this.billingAddress.address_street_1 = this.shippingAddress.address_street_1;
                this.billingAddress.address_street_2 = this.shippingAddress.address_street_2;
                this.billingAddress.address_city = this.shippingAddress.address_city;
                this.billingAddress.address_zip = this.shippingAddress.address_zip;
                this.billingAddress.state_id = this.shippingAddress.state_id;
                this.billingAddress.country_id = this.shippingAddress.country_id;
            } else if(reset) {
                this.billingAddress.first_name = '';
                this.billingAddress.last_name = '';
                this.billingAddress.email = '';
                this.billingAddress.phone = '';
                this.billingAddress.address_street_1 = '';
                this.billingAddress.address_street_2 = '';
                this.billingAddress.address_city = '';
                this.billingAddress.address_zip = '';
                this.billingAddress.state_id = '';
            }
        },

        saveShippingStep() {
            for(var idx in this.$refs.shippingAddressField) {
                this.$refs.shippingAddressField[idx].validate();
            }
            this.$nextTick().then(() => {
                if(!this.$el.querySelector('.validation-failed')) {
                    var selectedCountry = this.getSelectedShippingCountry,
                        selectedState = this.getSelectedShippingState;

                    this.shippingAddress.country_code = selectedCountry.country_code;
                    this.shippingAddress.country_name = selectedCountry.country_name;
                    if(selectedState) {
                        this.shippingAddress.state_code = selectedState.state_code;
                        this.shippingAddress.state_name = selectedState.state_name;
                    }

                    this.collectTotals();
                    if(this.previous_step) {
                        this.active_step = this.previous_step;
                    } else {
                        this.active_step = 2; // billing
                    }

                    this.short_shipping_address = this.shippingAddress.address_street_1 + ', ' +
                        this.shippingAddress.address_city + ', ' +
                        (this.shippingAddress.state_code || this.shippingAddress.country_name || '')
                    this.scrollToTop();
                } else {
                    this.scrollToError();
                }
            });
        },
        async saveBillingStep() {
            this.setSameAsShipping();
            await new Promise(resolve => {
                this.$nextTick(() => {
                    for(var idx in this.$refs.billingAddressField) {
                        this.$refs.billingAddressField[idx].validate();
                    }
                    resolve();
                });
            });
            
            this.loading = true;
            Promise.all([this.$nextTick(), stripe.createToken(this.card)]).then(() => {
                if(
                    (
                        (!this.same_as_shipping && !this.$el.querySelector('.validation-failed')) ||
                        this.same_as_shipping
                    )
                    && !this.card_error && this.card_type
                ) {
                    var selectedCountry = this.getSelectedBillingCountry,
                        selectedState = this.getSelectedBillingState;

                    this.billingAddress.country_code = selectedCountry.country_code;
                    this.billingAddress.country_name = selectedCountry.country_name;
                    if(selectedState) {
                        this.billingAddress.state_code = selectedState.state_code;
                        this.billingAddress.state_name = selectedState.state_name;
                    }

                    this.collectTotals();
                    if(this.previous_step) {
                        this.active_step = this.previous_step;
                    } else {
                        this.active_step = 3; // review
                    }
                } else {
                    this.scrollToError();
                }

                setTimeout(() => {
                    this.loading = false;
                }, 300);
            });
        },

        applyDiscountCode() {
            let validation = this.$refs.discountCodeField.validate();

            this.loading = true;
            this.$store
                .dispatch('checkout/applyDiscountCode', {
                    discountCode: this.discount_code
                })
                .then((message) => {
                    this.loading = false;
                    this.discount_code_error = '';
                    this.discount_code_message = message;
                })
                .catch((error) => {
                    this.loading = false;
                    this.discount_code_error = error;
                });
        },

        removeDiscountCode() {
            this.loading = true;
            this.$store
                .dispatch('checkout/removeDiscountCode')
                .then(() => {
                    this.loading = false;
                    this.discount_code = '';
                    this.discount_code_error = '';
                    this.discount_code_message = '';
                })
                .catch((error) => {
                    this.loading = false;
                });
        },

        getRowTotal(product) {
            return product.product_price * product.cart_product_quantity;
        },

        goToStep(step) {
            this.previous_step = this.active_step;
            this.active_step = step;
        },

        scrollToTop() {
            document.getElementById('main-wrapper').scrollTo(0,0);
        },

        scrollToError() {
            var errorElement = this.$refs.card.classList.contains("StripeElement--invalid") || (this.card_error && this.card_error.length > 0)
                ? this.$refs.card
                : this.$el.querySelector(".validation-failed");

            if (errorElement) {
                document
                    .getElementById("main-wrapper")
                    .scrollTo(0, errorElement.offsetTop - 90);
            }
        },

        goBack() {
            this.$router.back();
        },
        validateZipCode(value, country){
            return  this.validPostcode(value, country?.zipcode_validator);
        },
        moveTo(step) {
            if(this.active_step > step) {
                this.active_step = step;
            }
        }
    },
    watch: {
        'billingAddress.country_id': function() {
            this.billingAddress.state_id = null;
        },
        'shippingAddress.country_id': function() {
            this.shippingAddress.state_id = null;
        }
    }
};
</script>

<style lang="scss" scoped>
    .button.back-button {
        margin-bottom: 15px;
        border: 1px solid #efefef;
        &:after {
            top: 16px;
        }
    }
</style>